import useSWR from "swr";
import { Activity, CreditCard, Database, Package, Shield, Users as UsersIcon, Zap } from "lucide-react";
import { swrFetcher, type AdminStats } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { formatUsd } from "@/lib/format";

export function OverviewTab() {
  const { data: stats, isLoading } = useSWR<AdminStats>("/api/admin/stats", swrFetcher, {
    refreshInterval: 15000,
  });

  if (isLoading || !stats) return <p className="text-sm text-studio-muted">Loading stats…</p>;

  const cards = [
    { label: "Total Users", value: stats.users.total, sub: `${stats.users.active} active`, icon: UsersIcon, color: "text-sky-400" },
    { label: "Teams", value: stats.teams, sub: "organizations", icon: Shield, color: "text-violet-400" },
    { label: "Active Providers", value: stats.providers.active, sub: "connected", icon: Package, color: "text-studio-success" },
    { label: "Active Models", value: stats.models.active, sub: "deployed", icon: Database, color: "text-cyan-400" },
    { label: "Total Jobs", value: stats.jobs.total, sub: `${stats.jobs.completed} done / ${stats.jobs.failed} failed`, icon: Activity, color: "text-orange-400" },
    { label: "Pending Jobs", value: stats.jobs.pending, sub: "in progress", icon: Zap, color: "text-studio-warning" },
    { label: "USD Balance", value: formatUsd(stats.wallet.total_balance_usd, { decimals: 2 }) ?? "—", sub: `${formatUsd(stats.wallet.total_held_usd, { decimals: 2 }) ?? "—"} held`, icon: CreditCard, color: "text-studio-success" },
    { label: "Total Charged", value: formatUsd(stats.wallet.total_charged_usd, { decimals: 4 }) ?? "—", sub: "all-time USD", icon: CreditCard, color: "text-pink-400" },
  ];

  return (
    <div>
      <h1 className="mb-5 font-display text-xl font-bold tracking-tight sm:mb-6 sm:text-2xl">Dashboard Overview</h1>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 xl:grid-cols-4">
        {cards.map((c) => (
          <Card key={c.label} className="p-4">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs text-studio-muted">{c.label}</span>
              <c.icon className={cn("h-4 w-4", c.color)} />
            </div>
            <p className="break-words font-display text-2xl font-bold">{c.value}</p>
            <p className="mt-1 font-mono text-xs text-studio-faint">{c.sub}</p>
          </Card>
        ))}
      </div>
    </div>
  );
}
